import React from 'react'

export type LeftNavTheme = 'dark' | 'light'

const LEFT_NAV_THEME_STORAGE_KEY = 'leftNavTheme'
const DEFAULT_LEFT_NAV_THEME: LeftNavTheme = 'light'

interface LeftNavThemeContextValue {
  leftNavTheme: LeftNavTheme
  setLeftNavTheme: (theme: LeftNavTheme) => void
}

const isLeftNavTheme = (value: string | null): value is LeftNavTheme => {
  return value === 'dark' || value === 'light'
}

const getStoredLeftNavTheme = (): LeftNavTheme => {
  if (typeof window === 'undefined') return DEFAULT_LEFT_NAV_THEME

  try {
    const storedTheme = window.localStorage.getItem(LEFT_NAV_THEME_STORAGE_KEY)

    return isLeftNavTheme(storedTheme) ? storedTheme : DEFAULT_LEFT_NAV_THEME
  } catch {
    return DEFAULT_LEFT_NAV_THEME
  }
}

export const LeftNavThemeContext =
  React.createContext<LeftNavThemeContextValue>({
    leftNavTheme: DEFAULT_LEFT_NAV_THEME,
    setLeftNavTheme: () => null,
  })

export const LeftNavThemeProvider = (props: { children: React.ReactNode }) => {
  const [leftNavTheme, setLeftNavThemeState] = React.useState<LeftNavTheme>(
    getStoredLeftNavTheme
  )

  const setLeftNavTheme = React.useCallback((theme: LeftNavTheme) => {
    setLeftNavThemeState(theme)

    try {
      window.localStorage.setItem(LEFT_NAV_THEME_STORAGE_KEY, theme)
    } catch {
      return
    }
  }, [])

  return (
    <LeftNavThemeContext.Provider value={{ leftNavTheme, setLeftNavTheme }}>
      {props.children}
    </LeftNavThemeContext.Provider>
  )
}

export const useLeftNavTheme = () => React.useContext(LeftNavThemeContext)
